import { Inject, Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';
import * as ObjectActions from './device.actions';
import * as AppActions from '../app/actions';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { GeneralApiResponseInterface } from '../../layout/main/main.model';
import {
  ClientInterface,
  DeviceInterface,
  DeviceStatusInterface,
  DeviceTypeInterface,
  ConnectionProfileInterface,
  LineInterface,
  SiteInterface,
} from './device.model';
import { IotDeviceManagementPortalAppState } from '../iotDeviceManagementPortalApp.reducer';
import { Store } from '@ngrx/store';
import { PageFiltersAndSettingsService } from '../../shared/service/page-filters-and-settings/page-filters-and-settings.service';
import { FirmwareInterface } from '../firmware-manager/firmware-manager.model';
import { BulkResponseDataInterface } from '../../shared/model/interface/crud-response-interface.model';

@Injectable()
export class DeviceEffects {
  constructor(
    private actions$: Actions<ObjectActions.DeviceActions>,
    private http: HttpClient,
    private readonly store: Store<IotDeviceManagementPortalAppState>,
    private readonly pageFiltersAndSettings: PageFiltersAndSettingsService,
    @Inject('API_BASE_URL') private readonly baseUrl: string,
  ) {}

  private DEVICES_URLS = {
    GET: {
      DEVICE: '/device',

      DEVICE_TYPES: '/device-type',
      CLIENTS: '/clients',
      SITES: '/sites',
      LINES: '/lines',
      CONNECTION_PROFILES: '/device/connection-profiles',
    },
    POST: {
      DEVICE: '/device',
      DEVICE_BULK: '/device/bulk/edit',
      DEVICE_STATUS: '/device/connection-status',
      DEVICE_SCHEDULE_MESSAGE: '/device/schedule-message',
      REGISTER_DEVICES: '/device/register-devices',
      SEND_MESSAGE_TO_DEVICE: '/device/send-message',
      UPLOAD_FIRMWARE: '/device/upload-firmware',
      ABORT_UPLOAD_FIRMWARE: '/device/abort-upload-firmware',
    },
    PATCH: {
      DEVICE: '/device',
    },
    DELETE: {
      DEVICE: '/device',
      DEVICE_BULK: '/device/bulk/delete',
    },
  };
  private FIRMWARE_URLS = {
    GET: {
      FIRMWARE: '/firmware',
    },
  };

  @Effect()
  getDevices$ = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.GetDevices),
    switchMap((objectData: ObjectActions.GetDevices) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.DEVICES_URLS.GET.DEVICE}`;
      let params: HttpParams = new HttpParams();

      params = params
        .append('page', String(objectData.query.page))
        .append('limit', String(objectData.query.pageSize || 1000));

      if (objectData.query.order) {
        params = params
          .set('order', `${objectData.query.order}`)
          .set('orderType', `${objectData.query.orderType.toUpperCase()}`);
      }

      if (objectData.query.search !== undefined) {
        params = params.set('searchType', String('deviceSearch'));
        params = params.set('search', String(objectData.query.search));
      }

      if (objectData.query.startDate) {
        params = params.set('startDate', `${objectData.query.startDate}`).set('endDate', `${objectData.query.endDate}`);
      }

      if (objectData.query.deviceTypeIds) {
        params = params.set('deviceTypeIds', `${objectData.query.deviceTypeIds}`);
      }

      if (objectData.query.statusId) {
        params = params.set('statusId', `${objectData.query.statusId}`);
      }
      if (objectData.query.clientIds) {
        params = params.set('clientIds', `${objectData.query.clientIds}`);
      }

      // params = params.append('join', 'configs');
      return this.http
        .get<GeneralApiResponseInterface<DeviceInterface[]>>(url, {
          params,
        })
        .pipe(
          switchMap((response: GeneralApiResponseInterface<DeviceInterface[]>) => {
            return of(
              new ObjectActions.GetDevicesCompleted(response['data'], response['total']),
              new AppActions.HideLoader(),
            );
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  createDevice$ = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.CreateDevice),
    switchMap((objectData: ObjectActions.CreateDevice) => {
      return this.http
        .post<GeneralApiResponseInterface<DeviceInterface[]>>(`${this.baseUrl}${this.DEVICES_URLS.POST.DEVICE}`, {
          ...objectData.payload,
        })
        .pipe(
          switchMap((response) => {
            return of(new ObjectActions.CreateDeviceCompleted(response), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  getDevice$ = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.GetDevice),
    switchMap((device: ObjectActions.GetDevice) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.DEVICES_URLS.GET.DEVICE}/${device.deviceId}`;

      return this.http.get<GeneralApiResponseInterface<DeviceInterface>>(url).pipe(
        switchMap((device) => {
          return forkJoin<
            {
              device: Observable<GeneralApiResponseInterface<DeviceInterface>>;
              clients: Observable<GeneralApiResponseInterface<ClientInterface[]>>;
              sites: Observable<GeneralApiResponseInterface<SiteInterface[]>>;
              lines: Observable<GeneralApiResponseInterface<LineInterface[]>>;
            },
            'device' | 'clients' | 'sites' | 'lines'
          >({
            device: of(device),
            clients: this.http.get<GeneralApiResponseInterface<ClientInterface[]>>(
              `${this.baseUrl}${this.DEVICES_URLS.GET.CLIENTS}/${device.data.scwEnv}`,
            ),
            sites: this.http.get<GeneralApiResponseInterface<SiteInterface[]>>(
              `${this.baseUrl}${this.DEVICES_URLS.GET.SITES}`,
              {
                params: new HttpParams()
                  .append('scwEnv', String(device.data.scwEnv))
                  .append('client', device.data.client),
              },
            ),
            lines: this.http.get<GeneralApiResponseInterface<LineInterface[]>>(
              `${this.baseUrl}${this.DEVICES_URLS.GET.LINES}`,
              {
                params: new HttpParams()
                  .append('scwEnv', String(device.data.scwEnv))
                  .append('client', String(device.data.client))
                  .append('siteId', String(device.data.site)),
              },
            ),
          });
        }),
        switchMap((response) => {
          return of(
            new ObjectActions.GetClientsCompleted(response.clients.data),
            new ObjectActions.GetSitesCompleted(response.sites.data),
            new ObjectActions.GetLinesCompleted(response.lines.data),
            new ObjectActions.GetDeviceCompleted(response.device),
            new AppActions.HideLoader(),
          );
        }),
        catchError((errorRes) => {
          return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
        }),
      );
    }),
  );

  @Effect()
  getDeviceForMessageModal$ = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.GetDeviceForMessageModal),
    switchMap((device: ObjectActions.GetDeviceForMessageModal) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.DEVICES_URLS.GET.DEVICE}/${device.deviceId}`;

      return this.http.get<GeneralApiResponseInterface<DeviceInterface>>(url).pipe(
        switchMap((deviceRecord) => {
          return forkJoin<
            {
              device: Observable<GeneralApiResponseInterface<DeviceInterface>>;
              deviceStatus: Observable<GeneralApiResponseInterface<DeviceStatusInterface[]>>;
            },
            'device' | 'deviceStatus'
          >({
            device: of(deviceRecord),
            deviceStatus: this.http.post<GeneralApiResponseInterface<DeviceStatusInterface[]>>(
              `${this.baseUrl}${this.DEVICES_URLS.POST.DEVICE_STATUS}`,
              {
                devices: [device.deviceId],
              },
            ),
          });
        }),
        switchMap((response) => {
          return of(
            new ObjectActions.GetDeviceForMessageModalCompleted(
              response.device,
              response.deviceStatus,
              device.refreshCommand,
            ),
            new AppActions.HideLoader(),
          );
        }),
        catchError((errorRes) => {
          return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
        }),
      );
    }),
  );

  @Effect()
  getDeviceForMessageSchedulerModal$ = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.GetDevicesForMessageSchedulerModal),
    switchMap((device: ObjectActions.GetDevicesForMessageSchedulerModal) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.DEVICES_URLS.POST.DEVICE_STATUS}`;
      return this.http
        .post<GeneralApiResponseInterface<DeviceStatusInterface[]>>(url, {
          devices: device.deviceIds,
          force: true,
        })
        .pipe(
          switchMap((response) => {
            return of(
              new ObjectActions.GetDevicesForMessageSchedulerModalCompleted(response, device.refreshCommand),
              new AppActions.HideLoader(),
            );
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  sendDeviceScheduleRequests$ = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.SendDeviceScheduleRequests),
    switchMap((payload: ObjectActions.SendDeviceScheduleRequests) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.DEVICES_URLS.POST.DEVICE_SCHEDULE_MESSAGE}`;

      return this.http
        .post<GeneralApiResponseInterface<DeviceStatusInterface[]>>(url, {
          deviceIds: payload.deviceIds,
          message: payload.messageText,
        })
        .pipe(
          switchMap((response) => {
            return of(new ObjectActions.SendDeviceScheduleRequestsCompleted(response), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  updateDevice$ = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.UpdateDevice),
    switchMap((objectData: ObjectActions.UpdateDevice) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.DEVICES_URLS.PATCH.DEVICE}/${objectData.id}`;
      return this.http
        .patch<GeneralApiResponseInterface<DeviceInterface>>(url, {
          ...objectData.payload,
        })
        .pipe(
          switchMap((response) => {
            return of(new ObjectActions.UpdateDeviceCompleted(response), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  getDeviceTypes = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.GetDeviceTypes),
    switchMap(() => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.DEVICES_URLS.GET.DEVICE_TYPES}`;
      const params = new HttpParams().set('limit', '1000').set('page', '1');

      return this.http
        .get<GeneralApiResponseInterface<DeviceTypeInterface[]>>(url, {
          params,
        })
        .pipe(
          switchMap((response) => {
            return of(new ObjectActions.GetDeviceTypesCompleted(response['data']), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  getClients = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.GetClients),
    switchMap((payload: ObjectActions.GetClients) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.DEVICES_URLS.GET.CLIENTS}/${payload.scwEnv}`;
      const params = new HttpParams().set('limit', '1000').set('page', '1');

      return this.http
        .get<GeneralApiResponseInterface<ClientInterface[]>>(url, {
          params,
        })
        .pipe(
          switchMap((response) => {
            return of(new ObjectActions.GetClientsCompleted(response['data']), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  getSites = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.GetSites),
    switchMap((payload: ObjectActions.GetSites) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.DEVICES_URLS.GET.SITES}`;
      const params = new HttpParams().append('scwEnv', String(payload.scwEnv)).append('client', String(payload.client));

      return this.http
        .get<GeneralApiResponseInterface<SiteInterface[]>>(url, {
          params,
        })
        .pipe(
          switchMap((response) => {
            return of(new ObjectActions.GetSitesCompleted(response['data']), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  getLines = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.GetLines),
    switchMap((payload: ObjectActions.GetLines) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.DEVICES_URLS.GET.LINES}`;
      const params = new HttpParams()
        .append('scwEnv', String(payload.scwEnv))
        .append('client', String(payload.client))
        .append('siteId', String(payload.siteId));

      return this.http
        .get<GeneralApiResponseInterface<LineInterface[]>>(url, {
          params,
        })
        .pipe(
          switchMap((response) => {
            return of(new ObjectActions.GetLinesCompleted(response['data']), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  getConnectionProfiles = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.GetConnectionProfiles),
    switchMap((payload: ObjectActions.GetConnectionProfiles) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.DEVICES_URLS.GET.CONNECTION_PROFILES}`;

      return this.http.post<GeneralApiResponseInterface<ConnectionProfileInterface[]>>(url, {}).pipe(
        switchMap((response) => {
          return of(new ObjectActions.GetConnectionProfilesCompleted(response['data']), new AppActions.HideLoader());
        }),
        catchError((errorRes) => {
          return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
        }),
      );
    }),
  );

  @Effect()
  RegisterDevices = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.RegisterDevices),
    switchMap((objectData: ObjectActions.RegisterDevices) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.DEVICES_URLS.POST.REGISTER_DEVICES}`;
      return this.http
        .post<GeneralApiResponseInterface<DeviceTypeInterface[]>>(url, {
          devices: objectData.payload,
        })
        .pipe(
          switchMap((response) => {
            return of(new ObjectActions.RegisterDevicesCompleted(response['data']), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  SendMessageToDevice = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.SendMessageToDevice),
    switchMap((objectData: ObjectActions.SendMessageToDevice) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.DEVICES_URLS.POST.SEND_MESSAGE_TO_DEVICE}`;
      return this.http
        .post<GeneralApiResponseInterface<DeviceTypeInterface[]>>(url, {
          macAddress: objectData.macAddress,
          message: objectData.message,
        })
        .pipe(
          switchMap((response) => {
            return of(new ObjectActions.SendMessageToDeviceCompleted(response), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  deleteWorkOrders = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.DeleteDevice),
    switchMap((objectData: ObjectActions.DeleteDevice) => {
      this.store.dispatch(new AppActions.ShowLoader());
      let url = `${this.baseUrl}${this.DEVICES_URLS.DELETE.DEVICE}`;

      if (Array.isArray(objectData.payload) && objectData.payload.length > 1) {
        const httpOptions = {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
          body: {
            devices: objectData.payload,
          },
        };
        url = `${this.baseUrl}${this.DEVICES_URLS.DELETE.DEVICE_BULK}`;
        return this.http.delete<GeneralApiResponseInterface<DeviceInterface[]>>(url, httpOptions).pipe(
          switchMap((response) => {
            return of(new ObjectActions.DeleteDeviceCompleted(response['data']), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
      }
      return this.http.delete<GeneralApiResponseInterface<DeviceInterface[]>>(`${url}/${objectData.payload[0]}`).pipe(
        switchMap((response) => {
          return of(new ObjectActions.DeleteDeviceCompleted(response['data']), new AppActions.HideLoader());
        }),
        catchError((errorRes) => {
          return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
        }),
      );
    }),
  );

  @Effect()
  getFirmwares$ = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.GetFirmwares),
    switchMap((objectData: ObjectActions.GetFirmwares) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.FIRMWARE_URLS.GET.FIRMWARE}`;
      let params: HttpParams = new HttpParams();

      params = params.append('page', String(1)).append('limit', String(1000));

      return this.http
        .get<GeneralApiResponseInterface<FirmwareInterface[]>>(url, {
          params,
        })
        .pipe(
          switchMap((response: GeneralApiResponseInterface<FirmwareInterface[]>) => {
            return of(new ObjectActions.GetFirmwaresCompleted(response['data']), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  getDeviceStatusesForGrid$ = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.GetDeviceStatusesForGrid),
    switchMap((device: ObjectActions.GetDeviceStatusesForGrid) => {
      this.store.dispatch(new AppActions.ShowLoader());
      const url = `${this.baseUrl}${this.DEVICES_URLS.POST.DEVICE_STATUS}`;
      return this.http
        .post<GeneralApiResponseInterface<DeviceStatusInterface[]>>(url, {
          devices: device.deviceIds,
          force: true,
        })
        .pipe(
          switchMap((response) => {
            return of(new ObjectActions.GetDeviceStatusesForGridCompleted(response), new AppActions.HideLoader());
          }),
          catchError((errorRes) => {
            return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
          }),
        );
    }),
  );

  @Effect()
  setDeviceBulkEdit$ = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.UpdateBulkDevice),
    switchMap((payload: ObjectActions.UpdateBulkDevice) => {
      this.store.dispatch(new AppActions.ShowLoader());

      const url = `${this.baseUrl}${this.DEVICES_URLS.POST.DEVICE_BULK}`;
      return this.http.patch<BulkResponseDataInterface>(url, { devices: payload.devices }).pipe(
        switchMap((response) => {
          return of(new ObjectActions.UpdateBulkDeviceCompleted(response), new AppActions.HideLoader());
        }),
        catchError((errorRes) => {
          return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
        }),
      );
    }),
  );

  @Effect()
  fetchDevicesByMac$ = this.actions$.pipe(
    ofType(ObjectActions.DeviceActionTypes.FetchDevicesByMac),
    switchMap((action: ObjectActions.FetchDevicesByMac) => {
      this.store.dispatch(new AppActions.ShowLoader());

      // Create an array of HTTP requests for each MAC address
      const requests = action.macs.map((mac) => {
        const params = new HttpParams()
          .set('page', '1')
          .set('limit', '50')
          .set('searchType', 'deviceSearch')
          .set('search', mac)
          .set('deviceTypeIds', '-1')
          .set('statusId', '-1')
          .set('clientIds', '-1');

        return this.http
          .get<GeneralApiResponseInterface<DeviceInterface[]>>(`${this.baseUrl}${this.DEVICES_URLS.GET.DEVICE}`, {
            params,
          })
          .pipe(
            map((res) => ({ mac, devices: res.data })),
            catchError(() => of({ mac, devices: [] })), // Handle individual failures gracefully
          );
      });

      // Execute all requests concurrently
      return forkJoin(requests).pipe(
        switchMap((results) => [new ObjectActions.FetchDevicesByMacCompleted(results), new AppActions.HideLoader()]),
      );
    }),
  );
}
